<script lang="ts" setup>
const { t } = useI18n();
useHead({
  title: t("p-trade-head-title"),
  meta: [
    {
      name: "description",
      content: t("p-trade-head-description"),
    },
    {
      name: "keywords",
      content: t("p-trade-head-keywords"),
    },
  ],
});
import type { WatchStopHandle } from "vue";
import { MIN_OFFER_AMOUNT_USD } from "@/shared/consts";
const RatesStore = useRatesStore();
const { rates } = storeToRefs(RatesStore);
const route = useRoute();
const router = useRouter();
const { width } = useWindowSize();
const { data: user } = useAuth();
const CurrencyStore = useCurrencyStore();
const { currencies } = storeToRefs(CurrencyStore);
const PaymentOptionsStore = usePaymentOptionsStore();
const { getBaseOptions, getMyOptions } = PaymentOptionsStore;
const activeIndex = computed<string>(() => {
  const path = route.path;
  if (path.includes("/orders/")) return "/trade/orders";
  return path;
});
const sendSelected = ref("");
const recieveSelected = ref("");
const paymentMethod = ref<string[]>([]);
const amount = ref("");
const verifiedALM = ref(false);

const tradePage = computed(
  () => route.path.includes("sell") || route.path.includes("buy")
);

await useLazyAsyncData(() => getBaseOptions());

const minAmountLimit = computed(() => {
  return (
    +rates.value?.USDT?.[route.query.recieve as string] * MIN_OFFER_AMOUNT_USD
  );
});

const minTradeAmountSearched = computed(() => {
  const amountPassed = +(amount.value || Infinity);
  return amountPassed <= minAmountLimit.value;
});

let currenciesWatcher: WatchStopHandle,
  filtersWatcher: WatchStopHandle,
  routeWatcher: WatchStopHandle;

onMounted(() => {
  currenciesWatcher = watch(
    currencies,
    async () => {
      if (currencies.value.length === 0) return;
      const send = "USDT";
      const recieve = "RUB";
      sendSelected.value = (route.query.send as string) || send;
      recieveSelected.value = (route.query.recieve as string) || recieve;
      amount.value = (route.query.amount as string) || "";

      if (route.query.option && route.query.option !== "") {
        paymentMethod.value.push(...(route.query.option as string[]));
      }
    },
    { immediate: true }
  );

  filtersWatcher = watch(
    [
      sendSelected,
      recieveSelected,
      paymentMethod,
      verifiedALM,
      amount,
      () => route.params.action,
    ],
    () => {
      if (currencies.value.length === 0) return;
      if (route.params.action !== "buy" && route.params.action !== "sell") {
        return;
      }

      const query: Record<string, string | string[]> = {
        send: sendSelected.value,
        recieve: recieveSelected.value,
      };

      if (paymentMethod.value) {
        query.option = paymentMethod.value;
      }
      if (verifiedALM.value) {
        query.almCheck = "1";
      }
      if (amount.value) {
        query.amount = amount.value;
      }

      router.replace({
        path: route.path,
        query,
      });
    },
    { immediate: true }
  );

  routeWatcher = watch(
    [() => route.params, () => route.query],
    () => {
      if (route.params.action !== "buy" && route.params.action !== "sell") {
        return;
      }
      if (!route.query.send || !route.query.recieve) {
        return;
      }
      const oppositeAction = route.params.action === "buy" ? "sell" : "buy";
      abortController?.abort("APR");
      abortController = new AbortController();
      const paramsFromQuery: ParamsFromQuery = route.query as any;
      if (minTradeAmountSearched.value) {
        router.replace({
          query: {
            ...(paramsFromQuery as any),
            amount: "",
          },
        });
      }
      getOffers(
        {
          ...paramsFromQuery,
          type: oppositeAction,
        },
        abortController.signal
      );
    },
    { immediate: true }
  );
  if (user.value) getMyOptions();
});

const OffersStore = useOffersStore();
const { getOffers } = OffersStore;
let abortController: AbortController | null = null;
interface ParamsFromQuery {
  send: string;
  recieve: string;
  option?: string[];
  almCheck?: boolean;
  amount?: number;
}
const filtersSelected = computed(() => {
  return (
    (route.query.option && route.query.option.length) ||
    (route.query.amount && route.query.amount !== "")
  );
});
provide("filtersSelected", filtersSelected);
const onClearFilters = () => {
  paymentMethod.value = [];
  amount.value = "";
};
onUnmounted(() => {
  currenciesWatcher?.();
  filtersWatcher?.();
  routeWatcher?.();
});
const localePath = useLocalePath();
</script>

<template>
  <div class="default-width">
    <div class="mb-4">
      <el-menu
        ref="menuRef"
        :default-active="activeIndex"
        background-color="#545c64"
        text-color="#fff"
        active-text-color="#FFC107"
        class="trade-selector"
        mode="horizontal"
        :ellipsis="!!user && width < 600"
        router
        :close-on-click-outside="true"
      >
        <el-menu-item :index="localePath('/trade/buy')">
          {{ t("p-trade-menu-buy") }}
        </el-menu-item>
        <el-menu-item :index="localePath('/trade/sell')">
          {{ t("p-trade-menu-sell") }}
        </el-menu-item>
        <li class="flex-grow"></li>
        <template v-if="user">
          <el-menu-item :index="localePath('/trade/orders')">{{
            t("p-trade-menu-orders")
          }}</el-menu-item>
          <el-menu-item :index="localePath('/trade/my-offers')">{{
            t("p-trade-menu-myOffers")
          }}</el-menu-item>
          <el-menu-item :index="localePath('/trade/profile')">{{
            t("p-trade-menu-paymentOptions")
          }}</el-menu-item>
        </template>
      </el-menu>
    </div>
    <Transition>
      <div class="mb-7" v-if="tradePage">
        <ClientOnly>
          <OffersFilters
            v-model:amount="amount"
            v-model:send-selected="sendSelected"
            v-model:payment-method="paymentMethod"
            v-model:recieve-selected="recieveSelected"
            :min-trade-amount-searched="minTradeAmountSearched"
            :min-amount-limit="minAmountLimit"
            use-available-pairs
            show-amount
          />
        </ClientOnly>
      </div>
    </Transition>
    <NuxtPage @clearFilters="onClearFilters" />
  </div>
</template>

<style scoped lang="scss">
.trade-selector {
  border-radius: 10px;
  overflow: hidden;
}
</style>
